'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useOrganization, useOrganizationList } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';
import {
  BuildingOfficeIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import useUserPermissions from '@/hooks/useUserPermissions';
import useOrgActiveStatus from '@/hooks/useOrgActiveStatus';

interface OnboardingOrgSwitcherProps {
  compact?: boolean;
  /**
   * When true, render the switcher regardless of admin role or membership
   * count. Used by InactiveAccountScreen so the user always has a way out.
   */
  alwaysShow?: boolean;
}

export default function OnboardingOrgSwitcher({ compact = false, alwaysShow = false }: OnboardingOrgSwitcherProps) {
  const t = useTranslations('onboarding.orgSwitcher');
  const { isAdmin } = useUserPermissions();
  const { organization: activeOrg } = useOrganization();
  const { userMemberships, isLoaded: orgsLoaded, setActive } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 10,
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const membershipClerkIds = useMemo(
    () => (userMemberships.data ?? []).map((m) => m.organization.id),
    [userMemberships.data],
  );
  const activeStatus = useOrgActiveStatus(membershipClerkIds);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownEstimate = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUpward(spaceBelow < dropdownEstimate && spaceAbove > spaceBelow);
  }, [isOpen]);

  if (!alwaysShow && !isAdmin) return null;
  if (!orgsLoaded) return null;
  if (!alwaysShow && (!userMemberships.data || userMemberships.data.length < 2)) return null;

  const handleSwitch = async (orgId: string) => {
    if (!setActive) return;
    if (activeStatus[orgId]?.isActive === false) return;
    if (activeOrg?.id === orgId) {
      setIsOpen(false);
      return;
    }
    await setActive({ organization: orgId });
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
        style={{
          borderColor: 'var(--color-border)',
          background: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
        }}
        aria-label={t('ariaLabel')}
      >
        {activeOrg?.imageUrl ? (
          <img
            src={activeOrg.imageUrl}
            alt={activeOrg.name}
            className="w-5 h-5 rounded flex-shrink-0"
          />
        ) : (
          <BuildingOfficeIcon
            className="w-5 h-5 flex-shrink-0"
            style={{ color: 'var(--color-text-secondary)' }}
          />
        )}
        {!compact && (
          <span className="truncate max-w-[160px] w-full">
            {activeOrg?.name || t('selectOrg')}
          </span>
        )}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
        />
      </button>

      {isOpen && (
        <div
          // Horizontal anchor depends on where the trigger sits:
          //   - compact: top-bar right group (mobile) → anchor right edge so
          //     the menu drops into the visible bar, not off the right side.
          //   - non-compact: branding-panel bottom-left (desktop) → anchor
          //     left edge so the menu opens INTO the screen rather than
          //     getting clipped on the left.
          className={`absolute ${compact ? 'right-0' : 'left-0'} min-w-[240px] rounded-xl border shadow-xl overflow-hidden z-50 ${
            openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="px-3 pt-2 pb-1">
            <p
              className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('switchOrganization')}
            </p>
          </div>
          <div className="p-1 max-h-[280px] overflow-y-auto">
            {userMemberships.data.map((membership) => {
              const isCurrent = activeOrg?.id === membership.organization.id;
              const isOrgInactive = activeStatus[membership.organization.id]?.isActive === false;
              return (
                <button
                  key={membership.organization.id}
                  type="button"
                  onClick={() => handleSwitch(membership.organization.id)}
                  disabled={isOrgInactive}
                  aria-disabled={isOrgInactive}
                  className={`w-full flex items-center justify-between px-2 py-2 rounded-lg text-sm transition-colors ${
                    isOrgInactive
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-[var(--color-surface-hover)]'
                  }`}
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {membership.organization.imageUrl ? (
                      <img
                        src={membership.organization.imageUrl}
                        alt={membership.organization.name}
                        className="w-5 h-5 rounded flex-shrink-0"
                      />
                    ) : (
                      <BuildingOfficeIcon
                        className="w-5 h-5 flex-shrink-0"
                        style={{ color: 'var(--color-text-secondary)' }}
                      />
                    )}
                    <span className="truncate">{membership.organization.name}</span>
                    {isOrgInactive && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{
                          background: 'var(--color-surface-hover)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {t('inactive')}
                      </span>
                    )}
                  </div>
                  {isCurrent && !isOrgInactive && (
                    <CheckIcon className="w-4 h-4 text-lime-500 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
